import React from "react";
import { AlertCircle, MessageSquarePlus, RefreshCw } from "lucide-react";
import type { ChatConversation } from "../../backend/api/chat";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { cn } from "../../lib/utils";
import {
  conversationPreview,
  conversationStamp,
  unreadLabel,
} from "./chatFormat";

type ConversationListProps = {
  conversations: ChatConversation[];
  /** Resolved elsewhere, because a direct message is named after the other person. */
  titleFor: (conversation: ChatConversation) => string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  currentUserId: string | null | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  /** Staff can start a conversation; clients cannot, so this is optional. */
  onStartConversation?: () => void;
  emptyMessage: string;
  className?: string;
};

export function ConversationList({
  conversations,
  titleFor,
  selectedId,
  onSelect,
  currentUserId,
  isLoading,
  error,
  onRetry,
  onStartConversation,
  emptyMessage,
  className,
}: ConversationListProps) {
  if (isLoading) {
    return (
      <div className={cn("space-y-1 p-2", className)} aria-busy="true">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-start gap-3 rounded-md p-3">
            <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          "flex flex-col items-center gap-3 px-6 py-10 text-center",
          className,
        )}
      >
        <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          Your conversations could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          Try again
        </Button>
      </div>
    );
  }

  if (!conversations.length) {
    return (
      <div
        className={cn(
          "flex flex-col items-center gap-3 px-6 py-10 text-center",
          className,
        )}
      >
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        {onStartConversation ? (
          <Button variant="outline" size="sm" onClick={onStartConversation}>
            <MessageSquarePlus className="mr-2 h-4 w-4" aria-hidden="true" />
            Start a conversation
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <ul className={cn("space-y-1 p-2", className)}>
      {conversations.map((conversation) => {
        const unread = conversation.unread_count > 0;
        const selected = conversation.id === selectedId;
        return (
          <li key={conversation.id}>
            <button
              type="button"
              onClick={() => onSelect(conversation.id)}
              aria-current={selected ? "true" : undefined}
              className={cn(
                "flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected && "bg-accent text-accent-foreground",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "truncate text-sm",
                      unread ? "font-semibold" : "font-medium",
                    )}
                  >
                    {titleFor(conversation)}
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {conversationStamp(conversation.last_message_at)}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span
                    className={cn(
                      "truncate text-xs",
                      unread ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {conversationPreview(conversation, currentUserId)}
                  </span>
                  {unread ? (
                    <Badge className="ml-auto shrink-0 px-1.5 py-0 text-[11px] leading-5">
                      {unreadLabel(conversation.unread_count)}
                    </Badge>
                  ) : null}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
